import React, {Fragment, useCallback, useEffect, useRef} from 'react';
import {useTheme} from "@mui/material/styles";
import makeStyles from "@mui/styles/makeStyles";
import {useTranslation} from "react-i18next";
import {useDispatch, useSelector} from "react-redux";
import config from "../../app/config";
import _ from "lodash";
import moment from "moment";
import Button from "@mui/material/Button";
import ButtonGroup from "@mui/material/ButtonGroup";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import {DragDropContext, Draggable, Droppable} from "react-beautiful-dnd";
import {currentMap, updatePatientsData, wardPatients} from "../../store/reducers/ward";
import PatientInList from "./patientInList";
import Grid from "@mui/material/Grid";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ArrowDropUpIcon from "@mui/icons-material/ArrowDropUp";
import {hasPermission, Permissions} from "../../store/reducers/user";
import {TextField, ToggleButtonGroup, Tooltip} from "@mui/material";
import ToggleButton from "@mui/material/ToggleButton";
import IconButton from "@mui/material/IconButton";
import SettingsIcon from '@mui/icons-material/Settings';
import PriorityOptions from "../utils/priorityOptions";
import Dialog from "@mui/material/Dialog";
import {agylePref, saveAgylePreference} from "../../store/reducers/preferences";
import Divider from "@mui/material/Divider";
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import FilterAltOffIcon from '@mui/icons-material/FilterAltOff';
import FilterListIcon from '@mui/icons-material/FilterList';
import Popover from "@mui/material/Popover";
import DictParser from "../../util/dictParser";
import ExploreOffIcon from '@mui/icons-material/ExploreOff';
import {handleListName} from "./dashboard";
import {setSnackbar} from "../../store/reducers/globals";
import AgyleSelectField from "../../components/ui/selector/agyleSelectField";
import InformationBox from "../../components/other/informationBox";
import {useHistory} from "react-router-dom";
import clsx from "clsx";
import AgyleButton from "../../components/ui/buttons/agyleButton";
import usePrevState from '../../hooks/usePrevState';
import useOpenState from '../../hooks/useOpenState';
import MessageBox from '../../util/messageBox';
import {getTableScrollbar} from '../../util/scrollbar';

const useStyles = makeStyles((theme) => ({
    dialogRoot: {
        backgroundColor: theme.palette.background.paper,
        fontWeight: "500",
        fontSize: "0.875rem",
    },
    sortTitle: {
        cursor: "pointer",
        display: "flex",
        alignItems: "center"
    },
    filterMode: {
        display: "inline-block",
    },
    stickyHeader: {
        position: "sticky",
        top: -16,
        backgroundColor: theme.palette.background.paper,
    },
    input: {
        fontSize: "15px",
        margin: 8,
        backgroundColor: theme.palette.background.paper,
        borderRadius: "6%"
    },
    button: {
        width: 170
    },
    zoneFilter: {
        minWidth: 200
    },
    row: {
        display: "flex",
        flexWrap: "nowrap",
        flexDirection: "row",
        alignItems: "center",
        marginTop: 1,
    }
}));

function createColumn(name, description, order, sortable, sortField, sortFiledType, filter, filterType, gridWidth, active = true) {
    return {name, description, order, sortable, sortField, sortFiledType, filter, filterType, gridWidth, active};
}

const initColumns = [
    createColumn("total", "No", 1, false, null, null, null, "includes", 1),
    createColumn("priority", "Priority", 1, false, null, null, null, "includes", 1),
    createColumn("link", "Link", 2, false, null, null, null, "includes", 1),
    createColumn("name", "Patient Name", 3, true, "patient.familyName", "ptName", null, "includes", 3),
    createColumn("safeguardingLevel", "Safeguarding Level", 4, false, null, null, null, "includes", 2, config?.safeguardingLevelMandatory),
    createColumn("age", "Age", 5, true, "patient.dob", "moment", null, "includes", 1),
    createColumn("gender", "Gender", 6, true, "patient.gender", "string", null, "includes", 3),
    createColumn("area", "Area", 7, true, "area", "ward", null, "includes", 3),
    createColumn("bed", "Bed", 8, true, "bed", "bed", null, "includes", 3),
    createColumn("nhsNumb", "Hospital Number", 9, false, null, null, null, "includes", 3),
    createColumn("AEComplaint", "Chief Complaint", 10, true, "triage.chiefComplaint", "chief", null, "includes", 6),
    createColumn("time", "Time", 11, true, "admission.startDate", "moment", null, "includes", 2),
    createColumn("obs", "NEWS / PEWS", 12, true, "patient.lastObservation.totalScore", null, null, "includes", 2),
    createColumn("triage", "Triage", 13, true, "triage.discriminator.priority", null, null, "includes", 2),
    createColumn("clinician", "Current Clinician", 14, true, "currentClinician.__description", "string", null, "includes", 4),
    createColumn("seniorReview", "Senior Review", 15, true, "seniorReview", "moment", null, "includes", 4),
    createColumn("assessClinician", "Rapid Assessment Clinician", 16, true, "seen.who.__description", "string", null, "includes", 5),
    createColumn("clinicalConclusion", "Clinical Conclusion", 17, true, "clinicalConclusion.status", "string", null, "includes", 5, config?.clinicalConclusionPriorityList || false),
    createColumn("diagnosis", "Diagnosis", 18, true, "clerking.diagnosis", "string", null, "includes", 6, config?.diagnosisTreatmentAndInvestigationInList || false),
    createColumn("treatment", "Treatment", 19, true, "clerking.treatmentAndManagementPlan", "string", null, "includes", 6, config?.diagnosisTreatmentAndInvestigationInList || false),
    createColumn("investigations", "Investigations", 20, true, "clerking.investigations", "string", null, "includes", 6, config?.diagnosisTreatmentAndInvestigationInList || false),
    createColumn("referrals", "Referrals", 21, true, "studies.referrals.ReferralsToOtherServices", "referrals", null, "includes", 3),
    createColumn("tasks", "Tasks", 22, true, "tasks", "string", null, "includes", 6),
    createColumn("flags", "Flags", 23, true, "metadata.patientFlags", "string", null, "includes", 2, config?.mentalHealthFlagEnabled || false),
    createColumn("streamingType", "Streaming Type", 23, true, "metadata.healthcareServiceId", "string", null, "includes", 2, true),
    createColumn("nursingPlan", "Nursing Plan", 24, true, "studies.nursingPlanNote.notes", "string", null, "includes", 6, config?.nurseMedicalPlanAvailable || false),
    createColumn("medicalPlan", "Medical Plan", 25, true, "studies.medicalPlan.notes", "string", null, "includes", 6, config?.nurseMedicalPlanAvailable || false),
    createColumn("treatmentPlan", "Treatment Plan", 26, true, "studies.treatmentPlan.notes", "string", null, "includes", 6, config?.treatmentFormEnabled || false),
    createColumn("nextObs", "Next Obs Time", 27, true, "patient.lastObservation.nextObsDate", "moment", null, "includes", 2),
    createColumn("escalation", "Escalation", 28, true, "patient.lastObservation.metadata.extraOption1", null, null, "includes", 2),
    createColumn("bedReq", "Bed Request", 29, true, "", "bedReq", null, "includes", 3),
    createColumn("covid", "Infection", 30, false, null, "covid", null, "includes", 2),
    createColumn("sepsis", "Sepsis", 31, true, "metadata.sepsisPathway.status", "sepsis", null, "includes", 1),
    createColumn("histComplaint", "History Presenting Complaint", 32, false, null, null, null, "includes", 6),
    createColumn("dependency", "Dependency", 33, true, "triage.dependency.__code", null, null, "includes", 5),
    createColumn("linkAdmit", "Demographics", 34, false, null, null, null, "includes", 2, config?.priorityList?.enableDemographicUpdate || false),
    createColumn("ambulanceArrivalMode", "Arrival mode", 35, true, 'admission.arrivalModeDescription', 'ambulanceArrivalMode', null, "includes", 1, config?.priorityList?.arrivalModeInPriorityList || false),

];

const initColumnsUHD = [
    createColumn("total", "No", 1, false, null, null, null, "includes", 1),
    createColumn("priority", "Priority", 1, false, null, null, null, "includes", 1),
    createColumn("time", "Time", 2, true, "admission.startDate", "moment", null, "includes", 2),
    createColumn("bed", "Bed", 3, true, "bed", "bed", null, "includes", 3),
    createColumn("area", "Area", 3, true, "area", "ward", null, "includes", 3),
    createColumn("link", "Link", 4, false, null, null, null, "includes", 1),
    createColumn("name", "Patient Name", 5, true, "patient.familyName", "ptName", null, "includes", 3),
    createColumn("safeguardingLevel", "Safeguarding Level", 6, false, null, null, null, "includes", 2, config?.safeguardingLevelMandatory),
    createColumn("gender", "Gender", 7, true, "patient.gender", "string", null, "includes", 3),
    createColumn("age", "Age", 8, true, "patient.dob", "moment", null, "includes", 1),
    createColumn("AEComplaint", "Chief Complaint", 9, true, "triage.chiefComplaint", "chief", null, "includes", 6),
    createColumn("flags", "Flags", 10, true, "metadata.patientFlags", "string", null, "includes", 2, config?.mentalHealthFlagEnabled || false),
    createColumn("streamingType", "Streaming Type", 10, true, "metadata.healthcareServiceId", "string", null, "includes", 2, true),
    createColumn("triage", "Triage", 11, true, "triage.discriminator.priority", null, null, "includes", 2),
    createColumn("obs", "NEWS / PEWS", 12, true, "patient.lastObservation.totalScore", null, null, "includes", 2),
    createColumn("clinician", "Current Clinician", 13, true, "currentClinician.__description", "string", null, "includes", 4),
    createColumn("referrals", "Referrals", 14, true, "studies.referrals.ReferralsToOtherServices", "referrals", null, "includes", 3),
    createColumn("treatmentPlan", "Treatment Plan", 15, true, "studies.treatmentPlan.notes", "string", null, "includes", 6, config?.treatmentFormEnabled || false),
    createColumn("seniorReview", "Senior Review", 16, true, "seniorReview", "moment", null, "includes", 4),
    createColumn("assessClinician", "Rapid Assessment Clinician", 17, true, "seen.who.__description", "string", null, "includes", 5),
    createColumn("nursingPlan", "Nursing Plan", 18, true, "studies.nursingPlanNote.notes", "string", null, "includes", 6, config?.nurseMedicalPlanAvailable || false),
    createColumn("medicalPlan", "Medical Plan", 19, true, "studies.medicalPlan.notes", "string", null, "includes", 6, config?.nurseMedicalPlanAvailable || false),
    createColumn("diagnosis", "Diagnosis", 20, true, "clerking.diagnosis", "string", null, "includes", 6, config?.diagnosisTreatmentAndInvestigationInList || false),
    createColumn("clinicalConclusion", "Clinical Conclusion", 21, true, "clinicalConclusion.status", "string", null, "includes", 5, config?.clinicalConclusionPriorityList || false),
    createColumn("nextObs", "Next Obs Time", 22, true, "patient.lastObservation.nextObsDate", "moment", null, "includes", 2),
    createColumn("escalation", "Escalation", 23, true, "patient.lastObservation.metadata.extraOption1", null, null, "includes", 2),
    createColumn("treatment", "Treatment", 24, true, "clerking.treatmentAndManagementPlan", "string", null, "includes", 6, config?.diagnosisTreatmentAndInvestigationInList || false),
    createColumn("investigations", "Investigations", 25, true, "clerking.investigations", "string", null, "includes", 6, config?.diagnosisTreatmentAndInvestigationInList || false),
    createColumn("bedReq", "Bed Request", 26, true, "", "bedReq", null, "includes", 3),
    createColumn("covid", "Infection", 27, false, null, "covid", null, "includes", 2),
    createColumn("nhsNumb", "Hospital Number", null, false, null, null, null, "includes", 3),
    createColumn("tasks", "Tasks", null, true, "tasks", "string", null, "includes", 6),
    createColumn("sepsis", "Sepsis", null, true, "metadata.sepsisPathway.status", "sepsis", null, "includes", 1),
    createColumn("histComplaint", "History Presenting Complaint", null, false, null, null, null, "includes", 6),
    createColumn("dependency", "Dependency", null, true, "triage.dependency.__code", null, null, "includes", 5),
    createColumn("linkAdmit", "Demographics", null, false, null, null, null, "includes", 2, config?.priorityList?.enableDemographicUpdate || false),
    createColumn("ambulanceArrivalMode", "Arrived by Ambulance", 35, true, 'admission.arrivalModeDescription', 'ambulanceArrivalMode', null, "includes", 1, config?.priorityList?.arrivalModeInPriorityList || false),
];

function PriorityPatients(props) {
    const classes = useStyles();
    const {t} = useTranslation();
    const dispatch = useDispatch();
    const theme = useTheme();
    const history = useHistory();

    const wardPatientsObject = useSelector(wardPatients);
    const newCurrentMap = useSelector(currentMap);

    // States
    const [patients, setPatients] = React.useState([]);
    const [currentPatientOrder, setCurrentPatientOrder] = React.useState([]); // To save current order of patients
    const [orgListPts, setOrgListPts] = React.useState([]);
    const [modified, setModified] = React.useState(false);
    const [isSaveConfirmOpen, openSaveConfirm, closeSaveConfirm] = useOpenState(false, true);
    const closeOnSave = useRef(false);

    // Status
    const [status, setStatus] = React.useState("all");
    const statusChange = useRef(false);
    const prevStatus = usePrevState(status);
    const listEdited = useRef(false);

    // Zone Filter
    const [zoneList, setZoneList] = React.useState([]);
    const [zoneFilter, setZoneFilter] = React.useState("");

    // Table
    const [columns, setColumns] = React.useState([]);
    const [sortOrder, setSortOrder] = React.useState([]);
    const [filter, setFilter] = React.useState(false);
    const [filterPopover, setFilterPopover] = React.useState({open: false, type: null});
    const anchorEl = React.useRef(null);

    //Settings
    const agylePreference = useSelector(agylePref);
    const [openConfig, setOpenConfig] = React.useState(false);
    const [showWarning, setShowWarning] = React.useState(false);

    useEffect(() => {
        listEdited.current = false;
        if (config?.priorityList?.defaultFilterZone) {
            if (config?.priorityList?.defaultFilterZone[status]) setZoneFilter(config?.priorityList?.defaultFilterZone[status]);
            else setZoneFilter('');
        }

        if (!_.isEmpty(config?.priorityList?.disableFilterOnOption)) {
            if (config?.priorityList?.disableFilterOnOption?.includes(status)) {
                setFilter(false);
                clearFilters();
            }
        }

        setSortOrder([]);
    }, [status]);

    // Init columns
    useEffect(() => {
        let init = _.filter(initColumns, col => col.active);
        if (agylePreference?.priorityColumnOrder && !_.isEmpty(agylePreference?.priorityColumnOrder)) {
            const cloneProperties = _.cloneDeep(agylePreference?.priorityColumnOrder);
            const loadData = [];

            _.forEach(init, col => {
                const find = _.find(cloneProperties, {id: col.name});
                loadData.push({
                    ...col,
                    order: find ? find.order : col.order
                });
            });
            init = [...loadData];
        } else {
             if(config.newDefaultLayoutPriorityList){
               init = _.filter(initColumnsUHD, col => col.active);
             }
        }
        setColumns([...init]);

        if (config.zoneSelectInPriorityList) {
            const opt = [];
            _.forEach(props.areas, area => {
                opt.push({
                    id: area.dataName,
                    name: handleListName(area.type === "ed" ? area.dataName : area.areaTitle),
                });
            });

            setZoneList(handleZoneListData(opt))
        }
    }, [newCurrentMap]);

    const handleZoneListData = (options) => {
        let opt = []
        
        if (config.modules?.map?.customMap) {
            opt.push({
                id: "ACA",
                name: "ACA"
            })

            opt.push({
                id: "UTC Zone",
                name: "UTC ZONE"
            })

            opt.push({
                id: "Only_ED",
                name: "ONLY ED"
            })

            if (newCurrentMap?.mapPath === "poole") {
                opt.push({
                    id: "PITSTOP",
                    name: "PITSTOP"
                })
            }
             else if (newCurrentMap?.mapPath === "rbh_new") {
                opt.push({
                    id: "RATS",
                    name: "RATS"
                })
                opt.push({
                    id: "CHILD_ED",
                    name: "CHILDREN'S ED"
                })
            }
        }

        let toRemove = [];

        if (newCurrentMap?.mapPath === "poole") {
            toRemove = [
                'ACA', 'BLUE WAIT', 'ACA WAITING', 'WAIT TRIAGE', 'RELATIVES', // ACA
                'M H', // In Majors A
                'PITSTOP', 'AMBULANCE COHORT LIST', 'AMBULANCE QUEUE', 'CORRIDOR LIST', // PitStop
                'AWAITING TRIAGE UTC', 'CHILD WAIT PRE TRIAGE', 'CONSULT A', 'CONSULT B', 'EXAM ROOM', 'POST TRIAGE UTC', 'TREAT ROOM', 'TRIAGE UTC', // UTC
                'TRIAGE', 'DISCHARGE', // Others
            ];
        }

        if (newCurrentMap?.mapPath === "rbh_new") {
            toRemove = [
                'ACA', 'ACA WAIT', 'MAJORS C E', // ACA
                'RATS', 'AMBULANCE WAIT', 'AMBULANCE CORRIDOR', 'UHD COHORT', // Rats
                "CHILDRENS WAITING ROOM", "CHILDRENS POST TRIAGE", "CHILDRENS SUBWAIT", "CED TRIAGE", "CED", // Childrens ED
                'ADULT TRIAGE', 'ISO 1', // only ED
                "AUTC", "AUTC ROOM", "CED", "UTC WAITING ROOM", "AUTC SUB WAIT", "CHILDRENS WAITING ROOM", // UTC
                'DISCHARGE PLANNED REVIEW', 'AWAITING DISCHARGE', "MAJ INTERVIEW" // Others
            ];
        }
        opt = [...opt, ..._.filter(options, p => !toRemove.includes(p.name))];
        return _.sortBy(opt, ["name"]);
    }

    function customZoneFilterCheck(filter) {
        let multiZoneCheck = [];

        // eslint-disable-next-line default-case
        switch (filter) {
            case 'ACA':
                if (newCurrentMap?.mapPath === 'poole')
                    multiZoneCheck = ['ACA', 'BLUE WAIT', 'ACA WAITING', 'WAIT TRIAGE', 'RELATIVES'];
                else
                    multiZoneCheck = ['ACA', 'ACA WAIT', 'MAJORS C E'];
                break;

            case 'MAJORS_A':
                multiZoneCheck = ['MAJORS A', 'M H', 'MH', 'MAJ INTERVIEW'];
                break;

            case 'CHILD_ED':
                multiZoneCheck = ["CHILDRENS WAITING ROOM", "CHILDRENS POST TRIAGE", "CHILDRENS SUBWAIT",
                     "CED TRIAGE", "CED"];
                break;

            case 'UTC Zone':
                if (newCurrentMap?.mapPath === 'rbh_new') {
                    multiZoneCheck = ["AUTC", "AUTC ROOM", "CED", "UTC WAITING ROOM", "AUTC SUB WAIT", "CHILDRENS WAITING ROOM"];
                    break;
                }
            // For Poole same as Only_ED
            // eslint-disable-next-line no-fallthrough
            case 'Only_ED':
                if (newCurrentMap?.mapPath === 'poole')
                    multiZoneCheck = ['SUB WAIT', 'AWAITING TRIAGE UTC', 'CHILD WAIT PRE TRIAGE', 'CONSULT A',
                    'CONSULT B', 'EXAM ROOM', 'POST TRIAGE UTC', 'TREAT ROOM', 'TRIAGE UTC', 'BEDAS'];
                else
                    multiZoneCheck = ['INITIAL ASSESSMENT', 'AWAITING TRIAGE', 'AMBULANCE WAIT', 'AMBULANCE CORRIDOR', 'MAJORS WAIT',
                    'RATS', 'ISO 1', 'ACA', 'ACA WAIT', 'MAJORS C E', 'MAJORS A', 'MAJORS B', 'MAJ INTERVIEW', 'RESUS',
                    'CHILDRENS POST TRIAGE', 'CHILDRENS SUBWAIT', 'CED', 'ADULT TRIAGE', 'CED TRIAGE'];
                break;

            case 'PITSTOP':
                multiZoneCheck = ['PITSTOP', 'AMBULANCE COHORT LIST', 'AMBULANCE QUEUE', 'CORRIDOR LIST'];
                break;

            case 'RATS':
                multiZoneCheck = ["RATS", "ISO 1", "AMBULANCE WAIT", "AMBULANCE CORRIDOR", "UHD COHORT"];
                break;
        }

        const zones = _.filter(props.areas, zone => {
            const name = handleListName(zone.type === "ed" ? zone.dataName : zone.areaTitle);
            if (filter === "Only_ED" && newCurrentMap?.mapPath === 'poole') {
                if (!multiZoneCheck.includes(name) && name !== "DISCHARGE") return zone;
            } else {
                if (multiZoneCheck.includes(name)) return zone;
            }
        });

        const finalObj = {beds: []};
        _.forEach(zones, z => finalObj.beds.push(...z.beds));
        return finalObj;
    }

    function filterPatientsByZone(patientsClone) {
        if (config.zoneSelectInPriorityList && !_.isEmpty(zoneFilter)) {
            let findZone;
            if (zoneFilter === 'UTC Zone' || zoneFilter === 'Only_ED' || zoneFilter === 'PITSTOP' 
                || zoneFilter === 'RATS' || zoneFilter === 'ACA' || zoneFilter === 'MAJORS_A'
                || zoneFilter === 'CHILD_ED') {
                findZone = customZoneFilterCheck(zoneFilter);
            } else {
                findZone = _.find(props.areas, {dataName: zoneFilter});
            }
            const beds = [];

            // Build beds array
            _.forEach(findZone?.beds, bed => {
                if (bed?.parsed?.params?.[1])
                    beds.push(bed?.parsed?.params?.[1]);
            });

            _.remove(patientsClone, patient => {
                // If filter shows on starting list then check if pt have empty bed field
                const ptBed = patient?.bed || 'wlist1';
                return !beds.includes(ptBed);
            });
        }
    }

    useEffect(() => {
        // Order by:
        //
        // 1. Priority
        // 2. Triage score !
        // 3. Date of admission
        let patientsClone = [];
        _.forEach(_.cloneDeep(props.patients ? props.patients : wardPatientsObject), element => {
            let elementHidden = false;
            if (_.startsWith(element?.bed, "wlist") || !element?.bed) {
                if (element.admission?.locationDescription === "DISCHARGE") {
                    elementHidden = true;
                }
            }

            if (!elementHidden) {
                patientsClone.push(element);
            }
        });

        _.forEach(patientsClone, (patient) => {
            // we do need to deal with calculations all sort information first
            // I will add them ... to the clones just to make live easier we may
            // in the future decide otherwise ...

            const priority = patient?.metadata?.priority;
            const admissionDate = moment(patient.admission.startDate);
            const acuityScore =
                patient.triage && patient.triage.discriminator
                    ? patient.triage.discriminator.priority
                    : 5;
            const acuityColor =
                patient.triage && patient.triage.discriminator
                    ? patient.triage.discriminator.color
                    : "#2180F7";

            patient.sortData = {
                priority,
                admissionDate,
                acuityScore,
                acuityColor,
            };
        });

        const initSort = [];

        if (status === "nextToSeen") {
            initSort.push("sortData.priority")
            initSort.push("sortData.admissionDate")
        } else {
            initSort.push("sortData.admissionDate")
        }

        if (!config?.priorityList?.sortByLongestTime) {
            initSort.unshift("sortData.acuityScore");
            initSort.unshift("sortData.priority");
        }

        patientsClone = _.sortBy(patientsClone, initSort);

        if (props.fromAdmit && status !== "nextToSeen") patientsClone = patientsClone.reverse();

        setOrgListPts(patientsClone);

        _.remove(patientsClone, patient => {
            switch (status) {
                case "temp":
                    return !(patient?.patient?.tags && patient.patient.tags.includes("temporary"));
                case "notAll":
                    return !(patient.triage && patient.triage.chiefComplaint);
                case "waitTriage":
                    return !_.isEmpty(patient.triage?.discriminator);
                case "currentClinician":
                    return !_.isEmpty(patient?.currentClinician);
                case "referrals":
                    return !_.isEmpty(patient?.studies?.referrals?.ReferralsToOtherServices);
                case "tasks":
                    return !_.isEmpty(patient?.flags);
                case "nextToSeen":
                    if (config?.priorityList?.nextToSeenWithoutCurrentClinician) {
                        return !(_.isEmpty(patient?.currentClinician));
                    }
                    return !(_.isEmpty(patient?.currentClinician) && _.isEmpty(patient?.studies?.referrals?.ReferralsToOtherServices));
                default:
                    return false;
            }
        });
        filterPatientsByZone(patientsClone);

        _.forEach(patientsClone, (patient, index) => {
            patient.sortData.priority = patient?.metadata?.priority || null;
        });

        statusChange.current = prevStatus !== status;

        setPatients(patientsClone);
        setModified(listEdited.current);
    }, [status, zoneFilter, wardPatientsObject, props.patients]);

    // Update patient but leave current order of patients in priority list
    useEffect(() => {
        if (_.isEmpty(currentPatientOrder) || statusChange.current) {
            setCurrentPatientOrder(patients);
        } else {
            let patientsClone = _.cloneDeep(patients);
            let currentPatientClone = _.cloneDeep(currentPatientOrder);

            if (listEdited.current) {
                const mergedList = currentPatientClone.map((currentObj) => {
                    const matchingObj = _.find(patientsClone, { __id: currentObj.__id });
                    if (matchingObj) {
                        return { ...currentObj, ...matchingObj };
                    }
                });
                const newObjects = _.differenceBy(patientsClone, currentPatientClone, '__id');
                mergedList.push(...newObjects);
                patientsClone = mergedList
            }

            patientsClone = sortPatients(patientsClone);
            filterPatientsByZone(patientsClone);
            if (!_.isEqual(currentPatientOrder, patientsClone)) setCurrentPatientOrder(patientsClone);
        }
    }, [patients]);

    // Warning check
    useEffect(() => {
        let check = false;
        check = !_.isEmpty(zoneFilter);
        if (!check) check = !_.isEmpty(sortOrder);
        if (!check) {
            _.forEach(columns, c => {
                if (check) return;
                check = !_.isEmpty(c.filter);
            })
        }
        setShowWarning(check);
    }, [sortOrder, columns, zoneFilter, status]);

    // Sorting
    useEffect(() => {
        let patientsClone = _.cloneDeep(currentPatientOrder);
        if (!_.isEmpty(patientsClone) && !_.isEmpty(sortOrder) && canSort()) {
            patientsClone = sortPatients(patientsClone);
            setCurrentPatientOrder(patientsClone);
        }
    }, [sortOrder]);

    const sortPatients = (pts) => {
        let sortPts = pts;
        _.forEach(sortOrder, order => {
            sortPts = _.orderBy(pts, (patient) => {
                return sortByType(order.type, order.field, patient);
            }, [order.direction]);

            // _.forEach(sortPts, (patient, index) => {
            //     patient.sortData.priority = index + 1;
            // });
        });

        return sortPts;
    };

    const canSort = () => {
        if (!_.isEmpty(config?.priorityList?.disableSortOnOption)) {
            return !config?.priorityList?.disableSortOnOption?.includes(status);
        }
        return true;
    }

    const sortByType = (fieldType, field, patient) => {
        switch (fieldType) {
            case "string":
                return _.get(patient, field);
            case "moment": {
                const date = _.get(patient, field);
                return date ? moment(date) : null;
            }
            case "sepsis": {
                const sepsis = _.get(patient, field);
                return _.isUndefined(sepsis) ? -1 : sepsis === 0 ? 1 : -1;
            }
            case "covid": {
                const covid = _.get(patient, field);

                if (covid?.expired) {
                    if (moment(covid?.expired).isSameOrAfter(moment())) {
                        return !covid?.status ? -1 : covid.status === "none" ? 0 : covid.status === "suspected" ? 1 : 2;
                    }
                    return -1;
                } else {
                    return !covid?.status ? -1 : covid.status === "none" ? 0 : covid.status === "suspected" ? 1 : 2;
                }
            }
            case "referrals":
                return orderRef(_.get(patient, field));
            case "tasks":
                return orderFlags(_.get(patient, field));
            case "bed": {
                return findAreaBed("bed", patient, true);
            }
            case "ptName":
                return `${_.get(patient, "patient.familyName")} ${_.get(patient, "patient.firstName")}`;
            case "ward": {
                return findAreaBed("area", patient, true);
            }
            case "chief": {
                let chief = "";
                if (patient?.triage?.chiefComplaint) {
                    chief = patient?.triage?.chiefComplaint?.parentTerm;
                } else if (patient?.admission?.initialAEComplaintDescription) {
                    chief = patient?.admission?.initialAEComplaintDescription;
                }
                return chief;
            }

            case "ambulanceArrivalMode": {
                function checkAmbulance(str) {
                    const regex = /helicopter | ambulance/i;
                    return regex.test(str);
                }

                let result = "";
                const arrivalMode = patient?.admission?.arrivalModeDescription;

                if (checkAmbulance(arrivalMode)){
                    result = 'yes';
                } else {
                    result = 'no';
                }
                return result;
            }

            case "bedReq":
                if (config?.modules?.forms?.bedBooking?.enableBedRequestForm) {
                    if (!patient?.studies?.bedRequest) return '-';
                    const bedRequest = patient?.studies?.bedRequest;

                    let val = 'Requested';
                    if (bedRequest?.canceled) val = 'Cancelled';
                    else if (bedRequest?.ready) val = 'Ready';
                    else if (bedRequest?.allocated) val = 'Allocated';

                    return val;

                }
                return _.get(patient, "csmContactedTime.bed");

            default:
                return _.get(patient, field) >= 0 ? _.get(patient, field) : -1;
        }
    };

    const findSortValue = (filed) => {
        const find = _.find(sortOrder, {field: filed});
        return find ? find.direction : null;
    };

    const setSort = (field, type) => {
        let clone = _.cloneDeep(sortOrder);
        const index = _.findIndex(clone, {field});

        if (index !== -1) {
            if (clone[index].direction === "desc") {
                clone.splice(index, 1);
            } else {
                clone[index].direction = "desc";
            }
        } else {
            clone.unshift({type, field, direction: "asc"});
        }
        setSortOrder([...clone]);
    };

    const sortBy = (field, fieldType) => {
        if (field && canSort()) {
            setSort(field, fieldType);
            setModified(true);
        }
    };

    // Save columns configuration
    const handleChangeColumns = (activeColumns, disableColumns) => {
        const disabled = [];
        const active = [];

        for (let i = 0; i < activeColumns.length; i++) {
            active.push({
                ...activeColumns[i],
                order: i + 1
            });
        }

        _.forEach(disableColumns, col => {
            disabled.push(
                {
                    ...col,
                    order: null,
                }
            );
        });
        const newList = [...active, ...disabled];

        setColumns([...newList]);

        const userPref = [];
        _.forEach(newList, column => {
            userPref.push({
                id: column.name,
                order: column.order
            });
        });

        dispatch(saveAgylePreference({priorityColumnOrder: [...userPref]}));
        setOpenConfig(false);
    };

    const handleChangeZoneFilter = (val) => {
        setZoneFilter(val);
    }

    const handleAdmissionButton = () => {
        if (props.fromAdmit) props.onClose();
        else history.push('/registry')
    }

    // Restore Default Layout
    const setDefaultLayout = () => {
        let filtered;
            if(!config.newDefaultLayoutPriorityList){
                filtered = _.filter(initColumns, col => col.active);
            } else {
                filtered = _.filter(initColumnsUHD, col => col.active);
              }
                setColumns(filtered);
                dispatch(saveAgylePreference({priorityColumnOrder: null}));
                setOpenConfig(false);
    };

    const handleCloseSettings = () => {
        setOpenConfig(false);
    };

    const handleChangeStatus = (event, newStatus) => {
        if (newStatus) setStatus(newStatus);
    };

    const filterHandler = (event, type) => {
        const {value} = event.target;
        let clone = _.cloneDeep(columns);
        const index = _.findIndex(clone, {name: filterPopover.type});
        if (index !== -1) {
            if (type === "filter") {
                clone[index].filter = value;
            } else {
                clone[index].filterType = value;
            }
            setColumns([...clone]);
        }
    };

    const findValue = (type) => {
        const find = _.find(columns, {name: filterPopover.type});

        if (find) return find[type] ? find[type] : "";

        return "";
    };

    const getItemStyle = (isDragging, draggableStyle) => ({
        // some basic styles to make the items look a bit nicer
        userSelect: "none",

        // change background colour if dragging
        background: isDragging
            ? theme.palette.background.default
            : theme.palette.background.paper,

        // styles we need to apply on draggable
        ...draggableStyle,
    });

    const getListStyle = (isDraggingOver) => ({
        background: isDraggingOver ? "lightblue" : "lightgrey"
    });

    const reorder = (list, startIndex, endIndex) => {
        const result = Array.from(list);
        const [removed] = result.splice(startIndex, 1);
        result.splice(endIndex, 0, removed);

        return result;
    };

    const onDragEnd = (result) => {
        // dropped outside the list
        if (!result.destination) return;

        if (dispatch(hasPermission(Permissions.AGYLE_CAN_EDIT_PRIORTY_LIST))) {
            listEdited.current = true;
            let patientsClone = _.cloneDeep(currentPatientOrder);
            patientsClone = reorder(
                patientsClone,
                result.source.index,
                result.destination.index
            );

            // _.forEach(patientsClone, (patient, index) => {
            //     patient.sortData.priority = index + 1;
            // });

            setCurrentPatientOrder(patientsClone);
            setModified(true);
            setSortOrder([]);
        } else {
            dispatch(setSnackbar({type: "warning", message: t("User has no permission to edit priority list.")}));
        }
    };

    const confirmSave = (closeAfter) => {
        closeOnSave.current = closeAfter;
        openSaveConfirm();
    }

    const saveUpdates = () => {
        listEdited.current = false;
        let updates = [];
        let patientsClone = _.cloneDeep(currentPatientOrder);

        const fullPatientsListClone = [];
        _.forEach(_.cloneDeep(props.patients ? props.patients : wardPatientsObject), element => {
            let elementHidden = false;
            if (_.startsWith(element?.bed, "wlist") || !element?.bed) {
                if (element.admission?.locationDescription === "DISCHARGE") {
                    elementHidden = true;
                }
            }

            if (!elementHidden) {
                fullPatientsListClone.push(element);
            }
        });

        patientsClone = _.forEach(patientsClone, (patient, index) => patient.sortData.priority = index + 1);
        const diffArray = _.differenceBy(fullPatientsListClone, patientsClone, '__id');

        for (let patient of patientsClone) {
            updates.push({
                patientInWardId: patient.__id,
                set: [{'metadata.priority': patient.sortData.priority}],
            });
        }

        for (let patient of diffArray) {
            updates.push({
                patientInWardId: patient.__id,
                set: [{'metadata.priority': null}],
            });
        }

        dispatch(updatePatientsData(updates, (status) => {
                props.onSave(status);
                if (status) {
                    if (closeOnSave.current) props.onClose();
                }
            }),
        );
        closeSaveConfirm();
    };

    const saveDisabled = () => {
        if (config?.priorityList?.nextToSeenOnlySave) {
            return !modified || !dispatch(hasPermission(Permissions.AGYLE_CAN_EDIT_PRIORTY_LIST)) || status !== 'nextToSeen';
        } else {
            return !modified || !dispatch(hasPermission(Permissions.AGYLE_CAN_EDIT_PRIORTY_LIST));
        }
    };

    const orderRef = (referrals) => {
        if (!_.isEmpty(referrals)) {
            let completed = true;
            _.forEach(referrals, ref => {
                if (completed) {
                    if (!ref.ReferredToServiceAssessmentDate || !ref.ReferredToServiceAssessmentTime) {
                        completed = false;
                    }
                }
            });
            return completed ? 1 : 0;
        }
        return -1;
    };

    const orderFlags = flags => {
        if (!_.isEmpty(flags)) {
            let completed = true;
            _.forEach(flags, flag => {
                if (completed) {
                    if (!flag.name || !flag.value) {
                        completed = false;
                    }
                }
            });
            return completed ? 1 : 0;
        }
        return -1;
    }

    const getGridWidth = () => {
        const filteredColumns = _.filter(columns, col => {
            return !_.isNil(col.order);
        });
        let width = 0;
        _.forEach(filteredColumns, col => width += col.gridWidth);
        return width;
    };

    const renderColumn = (column) => {
        const excludedFilter = [{id: "priority"}, {id: "link"}, {id: "time"}, {id: "nextObs"}, {id: "covid"}, {id: "sepsis"}];
        const check = _.findIndex(excludedFilter, {id: column.name});
        if (column.sortable) {
            return (
                <Grid
                    item
                    xs={column.gridWidth}
                    key={column.name}
                    className={clsx(canSort() && classes.sortTitle)}
                    onClick={(event) => anchorEl.current = event.currentTarget}
                >
                    <div onClick={() => sortBy(column.sortField, column.sortFiledType)}>
                        {t(column.description)}
                        {canSort() &&
                            <>
                                {findSortValue(column.sortField) === "asc" && <ArrowDropDownIcon/>}
                                {findSortValue(column.sortField) === "desc" && <ArrowDropUpIcon/>}
                            </>
                        }
                    </div>
                    {(filter && check === -1) &&
                        <IconButton
                            size={"small"}
                            disableRipple={true}
                            onClick={() => setFilterPopover({open: true, type: column.name})}>
                            <FilterListIcon/>
                        </IconButton>
                    }
                </Grid>
            );
        }
        return (
            <Grid
                item
                xs={column.gridWidth}
                key={column.name}
                onClick={(event) => anchorEl.current = event.currentTarget}
            >
                {t(column.description)}
                {(filter && check === -1) &&
                    <IconButton
                        size={"small"}
                        disableRipple={true}
                        onClick={() => setFilterPopover({open: true, type: column.name})}>
                        <FilterListIcon/>
                    </IconButton>
                }
            </Grid>
        );
    };

    const sortColumn = useCallback((columns) => {
        const filteredColumns = _.filter(columns, col => {
            return !_.isNil(col.order);
        });

        return _.sortBy(filteredColumns, obj => {
            return obj.order;
        });
    }, [columns]);

    const clearFilters = () => {
        const tempArr = [];
        _.forEach(columns, col => {
            tempArr.push({
                ...col,
                filter: null
            });
        });
        setColumns([...tempArr]);
    };

    const clearSort = () => {
        setCurrentPatientOrder(orgListPts);
        setSortOrder([]);
    };

    const findAreaBed = (type, patient, sort) => {
        let ptBed = !patient?.bed ? "wlist1" : patient?.bed;
        let item;

        if (!_.isEmpty(props.areas)) {
            _.forEach(props.areas, area => {
                const index = _.findIndex(area.beds, bed => {
                    return bed?.parsed?.params[1] === ptBed;
                });
                if (index >= 0) {
                    item = {
                        area: area.areaTitle,
                        bed: _.upperCase(area.beds[index].parsed.name),
                    };
                }
            });
        } else if (!_.isEmpty(props.zones)) {
            _.forEach(props.zones, zone => {
                if (item) return;

                _.forEach(zone.beds, zoneBed => {
                    if (item) return;

                    if (zoneBed?.parsed?.params?.[1] === ptBed) {
                        item = {
                            area: zone?.areaTitle,
                            bed: _.upperCase(zoneBed?.parsed?.name),
                        };
                    }
                });
            });
        }

        if (item) return type === 'area' ? handleListName(item?.area) : item?.bed;
        else return sort ? null : "-";
    };

    // Filter mode check
    const checkFilterType = (field, type, filter) => {
        if (!field) return null;

        // eslint-disable-next-line default-case
        switch (type) {
            case "equals":
                return field === filter;
            case "includes":
                return _.toLower(field).includes(filter);
        }
    };

    const checkFilterRow = (name, filter, type, pt) => {
        const checkReferrals = (arr, filter) => {
            let check = filter === "-";
            _.forEach(arr, ref => {
                const text = ref?.ReferredToService_SnomedCt?.__description ? _.toLower(ref?.ReferredToService_SnomedCt?.__description) : "-";
                check = checkFilterType(text, type, filter);

                if (check) return false;
            });
            return check;
        };

        const checkFlags = (arr, filter) => {
            let check = filter === '-';
            _.forEach(arr, field => {
                const text = field?.name ? _.toLower(field.name) : "-";
                check = checkFilterType(text, type, filter);

                if (check) return false;
            })
            return check;
        }

        // eslint-disable-next-line default-case
        switch (name) {
            case "name": {
                const name = _.toLower(`${pt.patient?.familyName} ${pt.patient?.firstName}`);
                return checkFilterType(name, type, filter);
            }

            case "age": {
                let ageText = _.toLower("UNK");
                if (pt.patient?.dob) {
                    const dob = moment(pt.patient.dob);
                    const age = moment.duration(moment().diff(dob)).asYears();
                    ageText = _.toLower(Math.floor(age) + "");
                }
                return checkFilterType(ageText, type, filter);
            }

            case "gender": {
                const gender = _.toLower(pt.patient?.gender ? pt.patient?.gender : "-");
                return checkFilterType(gender, type, filter);
            }

            case "area": {
                const area = _.toLower(findAreaBed("area", pt));
                return checkFilterType(area, type, filter);
            }

            case "bed": {
                const bed = _.toLower(findAreaBed("bed", pt));
                return checkFilterType(bed, type, filter);
            }

            case "nhsNumb": {
                const nhs = _.toLower(pt.patient?.hospitalNumber ? pt.patient.hospitalNumber : "-");
                return checkFilterType(nhs, type, filter);
            }

            case "AEComplaint": {
                let chiefComplaint = _.toLower("NOT TRIAGED");
                if (pt?.triage?.chiefComplaint) {
                    chiefComplaint = _.toLower(DictParser.chiefComplaint(pt?.triage?.chiefComplaint, false));
                } else if (pt?.admission?.initialAEComplaintDescription) {
                    chiefComplaint = _.toLower(pt?.admission?.initialAEComplaintDescription);
                }
                return checkFilterType(chiefComplaint, type, filter);
            }

            case "obs": {
                const obs = _.toLower(pt.patient.lastObservation?.totalScore >= 0 ? pt.patient.lastObservation.totalScore + "" : "-");
                return checkFilterType(obs, type, filter);
            }

            case "triage": {
                const triage = _.toLower(pt.triage?.discriminator?.priority >= 0 ? pt.triage.discriminator.priority + "" : "-");
                return checkFilterType(triage, type, filter);
            }

            case "clinician": {
                const clinician = _.toLower(pt?.currentClinician ? pt?.currentClinician?.__description : "-");
                return checkFilterType(clinician, type, filter);
            }

            case "assessClinician": {
                const assessClinician = _.toLower(pt?.seen?.who ? pt?.seen?.who?.__description : "-");
                return checkFilterType(assessClinician, type, filter);
            }

            case "escalation": {
                const escalation = _.toLower(pt.patient?.lastObservation
                    ? pt.patient?.lastObservation?.metadata?.extraOption1 ? "Yes" : "No"
                    : "-");
                return checkFilterType(escalation, type, filter);
            }

            case "referrals": {
                return checkReferrals(pt?.studies?.referrals?.ReferralsToOtherServices, filter);
            }
            case "tasks": {
                return checkFlags(pt?.flags, filter);
            }

            case "bedReq": {
                if (config?.modules?.forms?.bedBooking?.enableBedRequestForm) {
                    if (pt?.studies?.bedRequest) {
                        let bed = "-";
                        if (pt?.studies?.bedRequest?.canceled) bed = "Canceled";
                        else if (pt?.studies?.bedRequest?.ready) bed = "Ready";
                        else if (pt?.studies?.bedRequest?.allocated) bed = "Allocated";
                        else if (pt?.studies?.bedRequest?.requested) bed = "Requested";
                        return checkFilterType(bed, type, filter);
                    }
                    return false;
                } else {
                    if (pt?.csmContactedTime) {
                        const bed = _.toLower(pt?.csmContactedTime?.bed === "req" ? "Requested" : pt?.csmContactedTime?.bed === "ready" ? "Ready" : "-");
                        return checkFilterType(bed, type, filter);
                    }
                    return false;
                }
            }

            case "histComplaint": {
                let presentingComplaint = "-";

                if (pt?.clerking?.historicPresentingComplaint) presentingComplaint = _.toLower(pt?.clerking?.historicPresentingComplaint);
                else if (pt?.studies?.seeAndTreat?.historyOfPresentingComplaint) presentingComplaint = _.toLower(pt?.studies?.seeAndTreat?.historyOfPresentingComplaint);
                else if (pt?.triage?.historicPresentingComplaint) presentingComplaint = _.toLower(pt?.triage?.historicPresentingComplaint);
                else if (pt?.metadata?.historyOfPresentingComplaint) presentingComplaint = _.toLower(pt?.metadata?.historyOfPresentingComplaint);

                return checkFilterType(presentingComplaint, type, filter);
            }

            case "dependency": {
                const dependency = _.toLower(pt?.triage?.dependency ? pt.triage?.dependency.__description : "-");
                return checkFilterType(dependency, type, filter);
            }

            case "ambulanceArrivalMode": {
                const arrivalMode = pt?.admission?.arrivalModeDescription;
                function checkAmbulance(str) {
                    const regex = /helicopter | ambulance/i;

                    return regex.test(str);
                }
                let result = "";

                if(checkAmbulance(arrivalMode)){
                    result = "yes";
                } else {
                    result = "no";
                }

                // let resume = _.toLower(pt.patient?.admission?.arrivalModeDescription ? pt.patient?.admission?.arrivalModeDescription : "-")

                return checkFilterType(result, type, filter);
            }
        }
    };

    const filterPatient = (patients, total = false) => {
        const onlyWithFilter = _.filter(columns, col => {
            return col.filter !== null && col.filter !== '';
        });
        if (!_.isEmpty(onlyWithFilter)) {
            const filteredPatient = [];
            _.forEach(patients, pt => {
                let check = true;
                _.forEach(onlyWithFilter, filterColumn => {
                    check = checkFilterRow(filterColumn.name, _.toLower(filterColumn.filter), filterColumn.filterType, pt);

                    if (!check) return false;
                });
                if (check) {
                    filteredPatient.push(pt);
                }
            });
            if (total) return filteredPatient?.length;
            return filteredPatient;
        }
        if (total) return patients?.length;
        return patients;
    };

    return (
        <Fragment>
            <DialogContent className={clsx(classes.dialogRoot, getTableScrollbar())} dividers>
                <div style={{width: (55 * getGridWidth())}}>
                    <Grid container
                          spacing={2}
                          direction="row"
                          className={classes.stickyHeader}
                          style={{minHeight: 60, borderBottom: "1px solid grey", fontWeight: "bold", zIndex: '2'}}
                          justifyContent="flex-start"
                          alignItems="center"
                          columns={getGridWidth()}
                    >
                        <Grid item xs={getGridWidth()} className={classes.row}>
                            <IconButton
                                size={"small"}
                                disableRipple={true}
                                onClick={() => setOpenConfig(true)}>
                                <SettingsIcon/>
                            </IconButton>

                            <Divider
                                orientation="vertical"
                                variant="middle"
                                flexItem
                                style={{margin: "0 10px"}}
                            />

                            <Tooltip title={"Show filters"}>
                                <IconButton
                                    size={"small"}
                                    disableRipple={true}
                                    onClick={() => setFilter(!filter)}>
                                    <FilterAltIcon/>
                                </IconButton>
                            </Tooltip>

                            <Tooltip title={"Clear all filters"}>
                                <IconButton
                                    size={"small"}
                                    disableRipple={true}
                                    onClick={clearFilters}
                                >
                                    <FilterAltOffIcon/>
                                </IconButton>
                            </Tooltip>

                            <Divider
                                orientation="vertical"
                                variant="middle"
                                flexItem
                                style={{margin: "0 10px"}}
                            />

                            <Tooltip title={"Clear all sorts"}>
                                <IconButton
                                    size={"small"}
                                    disableRipple={true}
                                    onClick={clearSort}
                                >
                                    <ExploreOffIcon/>
                                </IconButton>
                            </Tooltip>

                            {config.zoneSelectInPriorityList &&
                                <div className={classes.zoneFilter}>
                                    <AgyleSelectField
                                        label={t("Zone filter")}
                                        onChange={handleChangeZoneFilter}
                                        options={zoneList}
                                        value={zoneFilter}
                                    />
                                </div>
                            }

                            {(!props.simpleMode && config?.priorityList?.enableDemographicUpdate && dispatch(hasPermission(Permissions.AGYLE_CAN_ADMIT_PATIENT))) &&
                                <>
                                    <Divider
                                        orientation="vertical"
                                        variant="middle"
                                        flexItem
                                        style={{margin: "0 10px"}}
                                    />

                                    <AgyleButton
                                        onClick={handleAdmissionButton}
                                    >
                                        {t("Admission")}
                                    </AgyleButton>

                                    <Divider
                                        orientation="vertical"
                                        variant="middle"
                                        flexItem
                                        style={{margin: "0 10px"}}
                                    />
                                </>
                            }

                            {showWarning &&
                                <div style={{marginLeft: 5}}>
                                    <InformationBox type="warning" id="priority-list-warning" noMargin>
                                        {t("Caution Filter/Sort is applied - Not all patients might be displayed.")}
                                    </InformationBox>
                                </div>
                            }
                        </Grid>
                        {sortColumn(columns).map(column => (
                            renderColumn(column)
                        ))}
                    </Grid>

                    <DragDropContext onDragEnd={onDragEnd}>
                        <Droppable droppableId="droppable">
                            {(provided, snapshot) => (
                                <div
                                    {...provided.droppableProps}
                                    ref={provided.innerRef}
                                    style={getListStyle(snapshot.isDraggingOver)}
                                >
                                    {filterPatient(currentPatientOrder)?.map((wpo, index) => (
                                        <Draggable
                                            key={wpo?.__id}
                                            draggableId={wpo?.__id}
                                            index={index}
                                            isDragDisabled={props.simpleMode || !dispatch(hasPermission(Permissions.AGYLE_CAN_EDIT_PRIORTY_LIST))}
                                        >
                                            {(provided, snapshot) => (
                                                <div
                                                    ref={provided.innerRef}
                                                    {...provided.draggableProps}
                                                    {...provided.dragHandleProps}
                                                    style={getItemStyle(
                                                        snapshot.isDragging,
                                                        provided.draggableProps
                                                            .style
                                                    )}
                                                    onClick={() => {
                                                        if (!dispatch(hasPermission(Permissions.AGYLE_CAN_EDIT_PRIORTY_LIST))) {
                                                            dispatch(setSnackbar({type: "warning", message: t("User has no permission to edit priority list!")}));
                                                        }
                                                    }}
                                                >
                                                    <PatientInList
                                                        patient={wpo}
                                                        columns={sortColumn(columns)}
                                                        gridWidth={getGridWidth}
                                                        findAreaBed={findAreaBed}
                                                        openPatient={props.selectPatient}
                                                        fromAdmit={props.fromAdmit}
                                                        mergeTemporary={props.mergeTemporary}
                                                        total={filterPatient(currentPatientOrder, true)}
                                                        index={index}
                                                    />
                                                </div>
                                            )}
                                        </Draggable>
                                    ))}
                                    {provided.placeholder}
                                </div>
                            )}
                        </Droppable>
                    </DragDropContext>
                </div>
            </DialogContent>

            {!props.simpleMode &&
                <DialogActions>
                    <Fragment>
                        <div className={classes.filterMode}>
                            <ToggleButtonGroup
                                onChange={handleChangeStatus}
                                exclusive
                                value={status}
                                color="tertiary"
                            >
                                <ToggleButton
                                    size="small"
                                    className={classes.button}
                                    value="all">
                                    {t("Show All")}
                                </ToggleButton>

                                {(config?.priorityList?.enableWaitTriage && !dispatch(hasPermission(Permissions.AGYLE_PRIORITY_VIEW_FOR_JUNIOR_DOCTORS))) &&
                                    <ToggleButton
                                        size="small"
                                        className={classes.button}
                                        value="waitTriage">
                                        {t("Awaiting Triage")}
                                    </ToggleButton>
                                }

                                {(config?.priorityList?.enableTemporaryFilter && !dispatch(hasPermission(Permissions.AGYLE_PRIORITY_VIEW_FOR_JUNIOR_DOCTORS))) &&
                                    <ToggleButton
                                        size="small"
                                        className={classes.button}
                                        value="temp">
                                        {t("Temporary")}
                                    </ToggleButton>
                                }

                                {(!config?.priorityList?.disableOnlyTriageFilter && !dispatch(hasPermission(Permissions.AGYLE_PRIORITY_VIEW_FOR_JUNIOR_DOCTORS))) &&
                                    <ToggleButton
                                        size="small"
                                        className={classes.button}
                                        value="notAll">
                                        {t("Only Triage")}
                                    </ToggleButton>
                                }
                                {(!config?.priorityList?.disableCurrentClinicianFilter && !dispatch(hasPermission(Permissions.AGYLE_PRIORITY_VIEW_FOR_JUNIOR_DOCTORS))) &&
                                    <ToggleButton
                                        size="small"
                                        className={classes.button}
                                        value="currentClinician">
                                        {t("No Current Clinician")}
                                    </ToggleButton>
                                }
                                {!dispatch(hasPermission(Permissions.AGYLE_PRIORITY_VIEW_FOR_JUNIOR_DOCTORS)) &&
                                    <ToggleButton
                                        size="small"
                                        className={classes.button}
                                        value="referrals">
                                        {t("No Referrals")}
                                    </ToggleButton>
                                }
                                <ToggleButton
                                    size="small"
                                    className={classes.button}
                                    value="nextToSeen">
                                    {t("ED next to be seen")}
                                </ToggleButton>
                            </ToggleButtonGroup>
                        </div>
                        <ButtonGroup>
                            <Button
                                onClick={() => {
                                    props.onClose();
                                }}
                                color="tertiary"
                                variant={"text"}
                            >
                                {t("Close")}
                            </Button>
                            <Button
                                onClick={confirmSave.bind(this, true)}
                                variant={"text"}
                                color="tertiary"
                                disabled={saveDisabled()}
                            >
                                {t("Save and close")}
                            </Button>
                            <Button
                                onClick={confirmSave.bind(this, false)}
                                color="tertiary"
                                variant={"text"}
                                disabled={saveDisabled()}
                            >
                                {t("Save")}
                            </Button>
                        </ButtonGroup>
                    </Fragment>
                </DialogActions>
            }

            <Popover
                open={filterPopover.open}
                anchorEl={anchorEl.current}
                onClose={() => setFilterPopover({open: false, type: null})}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                }}
            >
                <Grid item xs={12} style={{margin: "8px 8px 0px 8px"}}
                >
                    <ToggleButtonGroup
                        color="primary"
                        size="small"
                        value={findValue("filterType")}
                        exclusive
                        fullWidth={true}
                        style={{height: 25}}
                        onChange={event => filterHandler(event, "filterType")}
                    >
                        <ToggleButton value="equals">{t("Equals")}</ToggleButton>
                        <ToggleButton value="includes">{t("Includes")}</ToggleButton>
                    </ToggleButtonGroup>
                </Grid>

                <Grid item xs={12}>
                    <TextField
                        size="small"
                        name="filter"
                        label={t("Filter")}
                        className={classes.input}
                        onChange={event => filterHandler(event, "filter")}
                        value={findValue("filter")}
                    />
                </Grid>
            </Popover>

            <Dialog
                open={openConfig}
                maxWidth="md"
                fullWidth={true}
                onClose={handleCloseSettings}
            >
                <PriorityOptions
                    onClose={handleCloseSettings}
                    columns={columns}
                    saveColumns={handleChangeColumns}
                    default={setDefaultLayout}
                />
            </Dialog>

            <MessageBox
                open={isSaveConfirmOpen}
                onClose={closeSaveConfirm}
                title={t("Confirmation")}
                haveAccept={{yes: t("Submit"), no: t("Close")}}
                onAccept={saveUpdates}
                message={t('Are you sure you want to recalculate and save the priority?')}
            />
        </Fragment>
    );
}

export default PriorityPatients;
